import React, { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { ShieldAlert, RefreshCw, Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { useRiskStore, RiskMetric, CorrelationEntry } from '../store/riskStore'
import { usePortfolioStore } from '../store/portfolioStore'
import { useThemeStore } from '../store/themeStore'
import { buttonClass } from './ui/Button';

const fmt = (v: number, decimals = 2) => v.toFixed(decimals)
const pct = (v: number) => `${(v * 100).toFixed(2)}%`

function corrColor(r: number, isDark: boolean): string {
  const c = Math.max(-1, Math.min(1, r))
  const alpha = Math.abs(c) * 0.75
  if (c > 0) return isDark ? `rgba(52,211,153,${alpha})` : `rgba(22,163,74,${alpha})`
  if (c < 0) return isDark ? `rgba(248,113,113,${alpha})` : `rgba(220,38,38,${alpha})`
  return 'transparent'
}

const RiskAnalysis: React.FC = () => {
  const { result, loading, compute, clear } = useRiskStore()
  const { portfolio } = usePortfolioStore()
  const { isDark, maskAmounts } = useThemeStore()
  const triggered = useRef(false)

  const MASK = '••••'

  const holdings = portfolio.map(h => ({
    symbol: h.symbol,
    name: h.name,
    port_api_type: h.port_api_type,
  }))

  // Auto-compute on first mount if portfolio is loaded
  useEffect(() => {
    if (triggered.current || holdings.length === 0) return
    triggered.current = true
    compute(holdings)
    return () => { triggered.current = false }
  }, [holdings.length])

  const handleRecompute = () => {
    clear()
    triggered.current = true
    compute(holdings)
  }

  const okMetrics = result?.metrics.filter(m => m.status === 'ok') ?? []
  const noDataMetrics = result?.metrics.filter(m => m.status === 'no_data') ?? []

  return (
    <>
      {/* Header */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-primary-500" />
              Risk Analysis
            </h2>
            {result && (
              <p className="text-xs text-gray-400 mt-0.5">
                คำนวณ {new Date(result.calculatedAt).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                {' · '}Risk-free rate {(result.riskFreeRate * 100).toFixed(1)}%
                {' · '}ข้อมูล 1 ปีย้อนหลัง (Yahoo Finance)
              </p>
            )}
          </div>
          <button
            onClick={handleRecompute}
            disabled={loading || holdings.length === 0}
            className={buttonClass('secondary', 'sm')}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary-600' : ''}`} />
            {loading ? 'กำลังคำนวณ...' : 'คำนวณใหม่'}
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">กำลังดึงข้อมูลจาก Yahoo Finance...</p>
          <p className="text-xs text-gray-400 dark:text-gray-400">อาจใช้เวลา 10–20 วินาที</p>
        </div>
      ) : !result ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-soft flex items-center justify-center mb-4">
            <ShieldAlert className="w-8 h-8 text-primary-400" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">กดปุ่ม "คำนวณใหม่" เพื่อเริ่ม</p>
        </div>
      ) : (
        <>
          <MetricsTable metrics={okMetrics} noData={noDataMetrics} isDark={isDark} maskAmounts={maskAmounts} mask={MASK} />
          {result.correlations.length > 0 && (
            <CorrelationMatrix
              correlations={result.correlations}
              symbols={okMetrics.map(m => m.symbol)}
              isDark={isDark}
            />
          )}
        </>
      )}
    </>
  )
}

// ── Metrics Table ─────────────────────────────────────────────────────────────
interface MetricsTableProps {
  metrics: RiskMetric[]
  noData: RiskMetric[]
  isDark: boolean
  maskAmounts: boolean
  mask: string
}

const MetricsTable: React.FC<MetricsTableProps> = ({ metrics, noData, maskAmounts, mask }) => {
  const m = (v: string) => maskAmounts ? mask : v

  const sharpeColor = (s: number | null | undefined) => {
    if (s == null) return ''
    if (s >= 1) return 'text-gain'
    if (s >= 0) return 'text-blue-600 dark:text-blue-400'
    return 'text-loss'
  }

  const betaColor = (b: number | null | undefined) => {
    if (b == null) return 'text-gray-400'
    if (b > 1.5) return 'text-loss'
    if (b > 1) return 'text-orange-500 dark:text-orange-400'
    if (b > 0) return 'text-blue-600 dark:text-blue-400'
    return 'text-purple-600 dark:text-purple-400'
  }

  return (
    <div>
      <div className="hidden sm:grid grid-cols-[1fr_90px_90px_90px_70px_90px] items-center gap-x-4 px-6 py-3 table-head">
        <span>หุ้น</span>
        <span className="text-right">Return/ปี</span>
        <span className="text-right">Volatility</span>
        <span className="text-right">Sharpe</span>
        <span className="text-right">Beta</span>
        <span className="text-right">Benchmark</span>
      </div>

      <div>
        {metrics.map((metric, i) => {
          const retPos = (metric.annualizedReturn ?? 0) >= 0
          return (
            <motion.div
              key={metric.symbol}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="group/row grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_90px_90px_90px_70px_90px] gap-x-4 items-center px-6 py-3.5 border-b border-gray-100 dark:border-gray-700/40 border-l-2 border-l-transparent hover:border-l-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-900/10 transition-all"
            >
              {/* Symbol */}
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white group-hover/row:text-primary-600 dark:group-hover/row:text-primary-400 transition-colors">
                  {metric.symbol}
                </p>
                <p className="text-xs text-gray-400 truncate max-w-[200px]">{metric.name}</p>
              </div>

              {/* Mobile: return + vol stacked */}
              <div className="flex flex-col items-end gap-0.5 sm:hidden">
                <span className={`text-sm font-bold tabular-nums ${retPos ? 'text-gain' : 'text-loss'}`}>
                  {m(`${retPos ? '+' : ''}${pct(metric.annualizedReturn ?? 0)}`)}
                </span>
                <span className="text-xs text-gray-400">{m(pct(metric.volatility ?? 0))}</span>
              </div>

              {/* Annual Return */}
              <div className="hidden sm:flex justify-end">
                <span className={`text-sm font-semibold tabular-nums flex items-center gap-1 ${retPos ? 'text-gain' : 'text-loss'}`}>
                  {retPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {m(`${retPos ? '+' : ''}${pct(metric.annualizedReturn ?? 0)}`)}
                </span>
              </div>

              {/* Volatility */}
              <div className="hidden sm:block text-right">
                <span className="text-sm font-semibold tabular-nums text-gray-700 dark:text-gray-300">
                  {m(pct(metric.volatility ?? 0))}
                </span>
              </div>

              {/* Sharpe */}
              <div className="hidden sm:block text-right">
                {metric.sharpe != null ? (
                  <span className={`text-sm font-semibold tabular-nums ${sharpeColor(metric.sharpe)}`}>
                    {m(fmt(metric.sharpe))}
                  </span>
                ) : <span className="text-xs text-gray-300 dark:text-gray-400">—</span>}
              </div>

              {/* Beta */}
              <div className="hidden sm:block text-right">
                {metric.beta != null ? (
                  <span className={`text-sm font-semibold tabular-nums ${betaColor(metric.beta)}`}>
                    {m(fmt(metric.beta))}
                  </span>
                ) : <Minus className="w-3 h-3 text-gray-300 ml-auto" />}
              </div>

              {/* Benchmark */}
              <div className="hidden sm:block text-right">
                <span className="text-xs text-gray-400 font-mono">{metric.benchmark ?? '—'}</span>
              </div>
            </motion.div>
          )
        })}

        {/* No-data holdings */}
        {noData.length > 0 && (
          <div className="px-6 py-3 border-t border-dashed border-gray-200 dark:border-gray-700/50">
            <p className="text-xs text-gray-400 dark:text-gray-400">
              ไม่มีข้อมูลเพียงพอ (th_fund / ข้อมูลน้อยกว่า 30 จุด):{' '}
              <span className="font-medium">{noData.map(m => m.symbol).join(', ')}</span>
            </p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700/40 flex flex-wrap gap-x-6 gap-y-1">
        {[
          { label: 'Sharpe ≥ 1', desc: 'ผลตอบแทนดีต่อความเสี่ยง', color: 'text-gain' },
          { label: 'Sharpe 0–1', desc: 'พอใช้', color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Sharpe < 0', desc: 'ต่ำกว่า risk-free', color: 'text-loss' },
          { label: 'Beta > 1', desc: 'เคลื่อนไหวมากกว่าตลาด', color: 'text-orange-500 dark:text-orange-400' },
          { label: 'Beta < 1', desc: 'เคลื่อนไหวน้อยกว่าตลาด', color: 'text-blue-600 dark:text-blue-400' },
        ].map(l => (
          <span key={l.label} className={`text-[11px] ${l.color}`}>
            <strong>{l.label}</strong> = {l.desc}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Correlation Matrix ────────────────────────────────────────────────────────
interface CorrelationMatrixProps {
  correlations: CorrelationEntry[]
  symbols: string[]
  isDark: boolean
}

const CorrelationMatrix: React.FC<CorrelationMatrixProps> = ({ correlations, symbols, isDark }) => {
  // Build lookup map
  const corrMap = new Map<string, number>()
  for (const c of correlations) {
    corrMap.set(`${c.symbolA}__${c.symbolB}`, c.correlation)
    corrMap.set(`${c.symbolB}__${c.symbolA}`, c.correlation)
  }
  const getCorr = (a: string, b: string) => {
    if (a === b) return 1
    return corrMap.get(`${a}__${b}`) ?? null
  }

  const cellCls = 'flex items-center justify-center text-xs font-semibold tabular-nums h-10 rounded transition-colors'

  return (
    <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-700/50">
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
        Correlation Matrix
        <span className="ml-2 text-xs font-normal text-gray-400">(ช่วง 1 ปี, รายวัน)</span>
      </p>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Header row */}
          <div className="flex" style={{ marginLeft: '80px' }}>
            {symbols.map(sym => (
              <div key={sym} className="w-16 flex-shrink-0 text-center">
                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 truncate block px-1">{sym}</span>
              </div>
            ))}
          </div>

          {/* Data rows */}
          {symbols.map(rowSym => (
            <div key={rowSym} className="flex items-center mb-1">
              {/* Row label */}
              <div className="w-20 flex-shrink-0 pr-2 text-right">
                <span className="text-[11px] font-bold text-gray-700 dark:text-gray-300 truncate block">{rowSym}</span>
              </div>
              {/* Cells */}
              {symbols.map(colSym => {
                const r = getCorr(rowSym, colSym)
                const isDiag = rowSym === colSym
                return (
                  <div key={colSym} className="w-16 flex-shrink-0 px-0.5">
                    <div
                      className={cellCls}
                      style={{
                        backgroundColor: isDiag
                          ? (isDark ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.15)')
                          : r !== null ? corrColor(r, isDark) : 'transparent',
                        color: r !== null && Math.abs(r) > 0.6
                          ? (isDark ? '#fff' : '#1f2937')
                          : (isDark ? '#d1d5db' : '#374151'),
                      }}
                      title={r !== null ? `${rowSym} × ${colSym}: ${r.toFixed(3)}` : 'ข้อมูลไม่เพียงพอ'}
                    >
                      {isDiag ? '1.00' : r !== null ? r.toFixed(2) : '—'}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Color scale legend */}
      <div className="flex items-center gap-3 mt-4">
        <span className="text-[11px] text-gray-400">−1</span>
        <div className="flex h-3 rounded overflow-hidden" style={{ width: '120px' }}>
          {[-1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1].map(v => (
            <div key={v} className="flex-1" style={{ backgroundColor: corrColor(v, isDark) || (isDark ? '#374151' : '#f3f4f6') }} />
          ))}
        </div>
        <span className="text-[11px] text-gray-400">+1</span>
        <span className="text-[11px] text-gray-400 ml-2">แดง = correlation ลบ, เขียว = correlation บวก</span>
      </div>
    </div>
  )
}

export default RiskAnalysis
